import { useEffect, useLayoutEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import useGroupSettingsStore from '@modules/digital-signage/components/group-settings/store/group-settings.store.ts';
import {
  GroupSettingsFormValues,
  UseGroupSettingsFormParams
} from '@modules/digital-signage/model/group-settings.model.ts';
import {
  countAddedDevices,
  getGroupDevices,
  isDefaultDeviceGroup,
  toDeviceOptions
} from '@modules/digital-signage/utils/digital-signage.utils.ts';
import { getGroupSettingsDefaultValues } from '@modules/digital-signage/components/group-settings/utils/group-settings.utils.ts';

export const useGroupSettingsForm = ({
  group,
  deviceId,
  deviceName
}: UseGroupSettingsFormParams) => {
  const { groupDetails, devicesList } = useGroupSettingsStore();

  const defaultValues = useMemo(
    () => getGroupSettingsDefaultValues({ group, deviceId, deviceName }),
    [group]
  );

  const methods = useForm<GroupSettingsFormValues>({
    mode: 'onTouched',
    defaultValues
  });

  const {
    control,
    resetField,
    getFieldState,
    formState: { defaultValues: formDefaults, isDirty }
  } = methods;
  const watchedScreen = useWatch({ control, name: 'assignedScreen' });
  const watchedPlaylist = useWatch({ control, name: 'assignedPlaylist' });
  const watchedGroupName = useWatch({ control, name: 'groupName' });
  const watchedDevices = useWatch({ control, name: 'devices' });
  const isDefaultGroup = isDefaultDeviceGroup(group);
  const hasGroupName = !!watchedGroupName?.trim();
  const isCreatingFromDefault = isDefaultGroup && hasGroupName;
  const addedDevicesCount = countAddedDevices(
    watchedDevices,
    formDefaults?.devices
  );

  // Fresh group data (GET /device-groups/{id}) and the devices list fill the
  // Device Group fields, unless the user has already edited them.
  useLayoutEffect(() => {
    // A device without a group keeps its own default device list.
    if (!group) return;
    const details = groupDetails?._id === group._id ? groupDetails : null;
    if (details && !getFieldState('groupName').isDirty) {
      resetField('groupName', { defaultValue: details.name ?? '' });
    }
    if (!getFieldState('devices').isDirty) {
      resetField('devices', {
        defaultValue: toDeviceOptions(
          getGroupDevices(
            group._id,
            details?.devices ?? group.devices,
            devicesList
          )
        )
      });
    }
  }, [groupDetails, devicesList]);

  useEffect(() => {
    if (isDefaultGroup && !hasGroupName) resetField('devices');
  }, [isDefaultGroup, hasGroupName]);

  return {
    methods,
    watchedScreen,
    watchedPlaylist,
    isDirty,
    isDefaultGroup,
    isCreatingFromDefault,
    addedDevicesCount
  };
};
